import React, { useEffect, useRef, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, MessageSquare } from "lucide-react";
import { ACHIEVEMENTS } from "../data/content";
import { Eyebrow } from "./shared";
import { OrbitRing, AstrolabeGlyph, PlanetDot } from "./motifs";

const DURATION = 6000;

export default function AchievementCarousel() {
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const [progress, setProgress] = useState(0);
  const raf = useRef();

  const next = useCallback(() => setIdx((i) => (i + 1) % ACHIEVEMENTS.length), []);
  const prev = () => setIdx((i) => (i - 1 + ACHIEVEMENTS.length) % ACHIEVEMENTS.length);

  useEffect(() => {
    if (paused || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    let start = performance.now();
    const tick = (t) => {
      const p = Math.min((t - start) / DURATION, 1);
      setProgress(p);
      if (p >= 1) { next(); start = performance.now(); }
      raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf.current);
  }, [paused, idx, next]);

  const a = ACHIEVEMENTS[idx];

  return (
    <section
      className="relative pt-12 pb-24 sm:pt-16 sm:pb-32 bg-softgold overflow-hidden"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      data-testid="achievement-carousel"
    >
      {/* orbit ring atmosphere */}
      <div className="pointer-events-none absolute -right-56 -top-32 opacity-45" aria-hidden>
        <div className="orbit-slow">
          <OrbitRing size={720} strokeOpacity={0.5} ticks />
        </div>
      </div>
      <div className="pointer-events-none absolute inset-0 constellation-grid opacity-30" aria-hidden />

      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <div className="flex items-end justify-between gap-6 mb-10">
          <Eyebrow n={7}>Past Accomplishments</Eyebrow>
          <div className="hidden sm:flex gap-2 shrink-0">
            <button onClick={prev} data-testid="carousel-prev" aria-label="Previous" className="w-11 h-11 grid place-items-center rounded-full bg-ivory/70 hover:bg-ivory text-caramel transition-colors">
              <ChevronLeft size={20} />
            </button>
            <button onClick={next} data-testid="carousel-next" aria-label="Next" className="w-11 h-11 grid place-items-center rounded-full bg-ivory/70 hover:bg-ivory text-caramel transition-colors">
              <ChevronRight size={20} />
            </button>
          </div>
        </div>

        <div className="relative min-h-[420px]">
          <AnimatePresence mode="wait">
            <motion.div
              key={a.key}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.5 }}
              className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center"
            >
              {/* conversation */}
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <span className="mt-1 w-9 h-9 shrink-0 grid place-items-center rounded-full bg-warmdark text-honey"><MessageSquare size={16} /></span>
                  <div className="rounded-2xl rounded-tl-sm bg-ivory/85 border border-ivory px-5 py-4 text-caramel/90">
                    <div className="font-mono-tech text-[10px] tracking-widest uppercase text-warmmuted mb-1">The leader</div>
                    {a.leader}
                  </div>
                </div>
                <div className="flex items-start gap-3 flex-row-reverse">
                  <span className="mt-1 w-9 h-9 shrink-0 grid place-items-center rounded-full bg-terracotta text-ivory font-display text-lg">B</span>
                  <div className="rounded-2xl rounded-tr-sm bg-terracotta text-ivory px-5 py-4">
                    <div className="font-mono-tech text-[10px] tracking-widest uppercase text-honey mb-1">Barbara</div>
                    {a.barbara}
                  </div>
                </div>
              </div>

              {/* outcome */}
              <div className="relative bg-ivory rounded-[1.4rem] border border-border p-7 sm:p-9 shadow-[0_20px_60px_rgba(74,51,34,0.12)] observatory-frame">
                <div className="absolute top-4 right-4"><AstrolabeGlyph size={14} color="#B08830" /></div>
                <div className="flex items-center gap-6">
                  <div>
                    <div className="font-mono-tech text-[9px] tracking-[0.32em] uppercase text-brass mb-1">Outcome</div>
                    <div className="font-display text-6xl sm:text-7xl text-terracotta leading-none">{a.metric}</div>
                    <div className="font-mono-tech text-[11px] tracking-wide uppercase text-warmmuted mt-2">{a.metricLabel}</div>
                  </div>
                  <img src={a.image} alt="" className="hidden sm:block w-28 h-28 object-cover rounded-2xl border border-brass/30" loading="lazy" />
                </div>
                <p className="mt-6 text-caramel/85 leading-relaxed">{a.impact}</p>
                <div className="mt-5 font-mono-tech text-xs tracking-wide uppercase text-olive flex items-center gap-2">
                  <PlanetDot /> {a.company} · {a.date}
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* progress + dots */}
        <div className="mt-10 flex items-center gap-4">
          <div className="flex gap-2">
            {ACHIEVEMENTS.map((s, i) => (
              <button
                key={s.key}
                onClick={() => setIdx(i)}
                data-testid={`carousel-dot-${i}`}
                aria-label={`Slide ${i + 1}`}
                className={`h-2 rounded-full transition-all duration-300 ${i === idx ? "w-8 bg-warmdark" : "w-2 bg-warmdark/30 hover:bg-warmdark/50"}`}
              />
            ))}
          </div>
          <div className="flex-1 h-1 rounded-full bg-warmdark/15 overflow-hidden">
            <div className="h-full bg-warmdark rounded-full" style={{ width: `${progress * 100}%` }} />
          </div>
        </div>
      </div>
    </section>
  );
}
